import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sparkles, TrendingUp, AlertTriangle, Info } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { InterviewPrepCategory } from "./readInterviewPrep";
import { getScoreColor } from "./scoreColor";

interface InterviewPrepContextProps {
	recommendation: string | null;
	strengths: string[];
	gaps: string[];
	categories: InterviewPrepCategory[];
}

export const InterviewPrepContext = ({
	recommendation,
	strengths,
	gaps,
	categories,
}: InterviewPrepContextProps) => {
	const { t } = useTranslation();

	const hasAnyContext = recommendation || strengths.length > 0 || gaps.length > 0 || categories.length > 0;
	if (!hasAnyContext) return null;

	return (
		<div className="space-y-4 mb-6">
			{recommendation && (
				<Card>
					<CardHeader className="pb-2">
						<CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
							<Sparkles className="h-4 w-4 text-primary" />
							{t("matching.interviewPrep.recommendation", "Overall recommendation")}
						</CardTitle>
					</CardHeader>
					<CardContent className="pt-0">
						<p className="text-sm leading-relaxed text-foreground whitespace-pre-line">
							{recommendation}
						</p>
					</CardContent>
				</Card>
			)}

			{(strengths.length > 0 || gaps.length > 0) && (
				<div className="grid md:grid-cols-2 gap-4">
					{strengths.length > 0 && (
						<Card>
							<CardHeader className="pb-2">
								<CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
									<TrendingUp className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
									{t("matching.interviewPrep.strengthsToHighlight", "Strengths to highlight")}
								</CardTitle>
							</CardHeader>
							<CardContent className="pt-0">
								<ul className="space-y-2">
									{strengths.map((s, i) => (
										<li
											key={i}
											className="text-sm text-foreground leading-relaxed flex gap-2"
										>
											<span className="text-emerald-600 dark:text-emerald-400 shrink-0">•</span>
											<span>{s}</span>
										</li>
									))}
								</ul>
							</CardContent>
						</Card>
					)}

					{gaps.length > 0 && (
						<Card>
							<CardHeader className="pb-2">
								<CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
									<AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
									{t("matching.interviewPrep.gapsToProbe", "Gaps to probe")}
								</CardTitle>
							</CardHeader>
							<CardContent className="pt-0">
								<ul className="space-y-2">
									{gaps.map((g, i) => (
										<li
											key={i}
											className="text-sm text-foreground leading-relaxed flex gap-2"
										>
											<span className="text-amber-600 dark:text-amber-400 shrink-0">•</span>
											<span>{g}</span>
										</li>
									))}
								</ul>
							</CardContent>
						</Card>
					)}
				</div>
			)}

			{categories.length > 0 && (
				<Card>
					<CardHeader className="pb-2">
						<CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
							<Info className="h-4 w-4 text-muted-foreground" />
							{t("matching.interviewPrep.categoryContext", "Category breakdown")}
						</CardTitle>
					</CardHeader>
					<CardContent className="pt-0">
						<div className="flex flex-wrap gap-2">
							{categories.map((cat) => {
								const colors = getScoreColor(cat.score);
								return (
									<Popover key={cat.name}>
										<PopoverTrigger asChild>
											<button
												type="button"
												className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors hover:shadow-sm ${colors.bgClass} ${colors.textClass} ${colors.borderClass}`}
											>
												<span>{cat.name}</span>
												<span className="font-semibold">{cat.score.toFixed(1)}</span>
											</button>
										</PopoverTrigger>
										<PopoverContent className="w-80 text-sm">
											<div className="font-semibold text-foreground mb-1">
												{cat.name}
											</div>
											<div className={`text-xs font-semibold mb-2 ${colors.textClass}`}>
												{t("matching.interviewPrep.score", "Score")}: {cat.score.toFixed(1)}/10
											</div>
											{cat.explanation && (
												<p className="text-muted-foreground leading-relaxed whitespace-pre-line">
													{cat.explanation}
												</p>
											)}
										</PopoverContent>
									</Popover>
								);
							})}
						</div>
					</CardContent>
				</Card>
			)}
		</div>
	);
};
